import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'PRE_ORDER' | 'DISCONTINUED';

interface Specification {
  key: string;
  value: string;
}

interface ProductFormData {
  modelId: string;
  code: string;
  sku?: string;
  name: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  price?: number;
  currency: string;
  priceOnRequest: boolean;
  stockStatus: StockStatus;
  minOrderQty: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
}

interface TradingProduct {
  id: string;
  modelId: string;
  code: string;
  sku?: string | null;
  name: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  specifications?: unknown;
  price?: number | null;
  currency: string;
  priceOnRequest: boolean;
  stockStatus: StockStatus;
  minOrderQty: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
  model?: {
    id: string;
    code: string;
    name: string;
    brand: {
      id: string;
      code: string;
      name: string;
    };
  };
}

interface ProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: TradingProduct;
  defaultModelId?: string;
}

export function ProductModal({ open, onOpenChange, product, defaultModelId }: ProductModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!product;

  const [specifications, setSpecifications] = useState<Specification[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ProductFormData>({
    defaultValues: {
      modelId: defaultModelId || '',
      code: '',
      sku: '',
      name: '',
      slug: '',
      shortDescription: '',
      description: '',
      price: undefined,
      currency: 'EUR',
      priceOnRequest: true,
      stockStatus: 'IN_STOCK',
      minOrderQty: 1,
      isActive: true,
      isFeatured: false,
      sortOrder: 0,
    },
  });

  const utils = trpc.useUtils();

  // Get brands for cascading dropdown
  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = brandsData?.brands ?? [];
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  // Get models for selected brand
  const { data: modelsData } = trpc.tradingModel.list.useQuery(
    { brandId: selectedBrandId },
    { enabled: !!selectedBrandId }
  );
  const models = modelsData?.models ?? [];

  const watchModelId = watch('modelId');
  const watchPriceOnRequest = watch('priceOnRequest');

  // Get next available code for new products
  const { data: nextCodeData } = trpc.tradingProduct.getNextCode.useQuery(
    watchModelId,
    { enabled: open && !isEditing && !!watchModelId }
  );

  const createMutation = trpc.tradingProduct.create.useMutation({
    onSuccess: () => {
      utils.tradingProduct.list.invalidate();
      utils.tradingModel.list.invalidate();
      showSuccessToast(t('common.created'));
      onOpenChange(false);
      reset();
      setSpecifications([]);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingProduct.update.useMutation({
    onSuccess: () => {
      utils.tradingProduct.list.invalidate();
      showSuccessToast(t('common.updated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (!open) return;

    if (product) {
      // Set brand from product's model
      if (product.model?.brand?.id) {
        setSelectedBrandId(product.model.brand.id);
      }

      reset({
        modelId: product.modelId,
        code: product.code,
        sku: product.sku || '',
        name: product.name,
        slug: product.slug,
        shortDescription: product.shortDescription || '',
        description: product.description || '',
        price: product.price || undefined,
        currency: product.currency,
        priceOnRequest: product.priceOnRequest,
        stockStatus: product.stockStatus,
        minOrderQty: product.minOrderQty,
        isActive: product.isActive,
        isFeatured: product.isFeatured,
        sortOrder: product.sortOrder,
      });

      // Load specifications
      if (product.specifications && typeof product.specifications === 'object' && !Array.isArray(product.specifications)) {
        setSpecifications(
          Object.entries(product.specifications as Record<string, string>).map(([key, value]) => ({ key, value: String(value) }))
        );
      } else {
        setSpecifications([]);
      }
    } else {
      setSelectedBrandId('');
      reset({
        modelId: defaultModelId || '',
        code: '',
        sku: '',
        name: '',
        slug: '',
        shortDescription: '',
        description: '',
        price: undefined,
        currency: 'EUR',
        priceOnRequest: true,
        stockStatus: 'IN_STOCK',
        minOrderQty: 1,
        isActive: true,
        isFeatured: false,
        sortOrder: 0,
      });
      setSpecifications([]);
    }
  }, [open, product, defaultModelId, reset]);

  // Set modelId after models are loaded when editing
  useEffect(() => {
    if (isEditing && product && models.length > 0) {
      // Only set if the model exists in the loaded models
      const modelExists = models.some(m => m.id === product.modelId);
      if (modelExists) {
        setValue('modelId', product.modelId);
      }
    }
  }, [isEditing, product, models, setValue]);

  // Auto-populate code, SKU and sortOrder when a model is picked for a new product.
  // Values are overridable: once set, the user can type over them.
  useEffect(() => {
    if (isEditing || !nextCodeData || !watchModelId) return;

    const selectedBrand = brands.find((b) => b.id === selectedBrandId);
    const selectedModel = models.find((m) => m.id === watchModelId);
    const brandCode = selectedBrand?.code ?? '';
    const modelCode = selectedModel?.code ?? '';
    const suggestedSku = brandCode && modelCode
      ? `${brandCode}${modelCode}${nextCodeData.code}`
      : '';

    setValue('code', nextCodeData.code);
    setValue('sortOrder', nextCodeData.sortOrder);
    if (suggestedSku) {
      setValue('sku', suggestedSku);
    }
  }, [nextCodeData, isEditing, watchModelId, selectedBrandId, brands, models, setValue]);

  // Auto-generate slug from name
  const watchName = watch('name');
  useEffect(() => {
    if (!isEditing && watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', slug);
    }
  }, [watchName, isEditing, setValue]);

  const addSpecification = () => {
    setSpecifications([...specifications, { key: '', value: '' }]);
  };

  const removeSpecification = (index: number) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...specifications];
    const spec = updated[index];
    if (spec) {
      spec[field] = value;
      setSpecifications(updated);
    }
  };

  const onSubmit = (data: ProductFormData) => {
    // Convert specifications array to object
    const specsObject = specifications.reduce((acc, spec) => {
      if (spec.key.trim()) {
        acc[spec.key.trim()] = spec.value;
      }
      return acc;
    }, {} as Record<string, string>);

    const payload = {
      modelId: data.modelId,
      code: data.code,
      name: data.name,
      slug: data.slug,
      currency: data.currency,
      priceOnRequest: data.priceOnRequest,
      stockStatus: data.stockStatus,
      minOrderQty: data.minOrderQty,
      isActive: data.isActive,
      isFeatured: data.isFeatured,
      sortOrder: data.sortOrder,
      sku: data.sku || undefined,
      shortDescription: data.shortDescription || undefined,
      description: data.description || undefined,
      price: data.priceOnRequest ? undefined : data.price,
      specifications: Object.keys(specsObject).length > 0 ? specsObject : undefined,
    };

    if (isEditing && product) {
      updateMutation.mutate({ id: product.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('products.editProduct') : t('products.addProduct')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('products.editProduct') : t('products.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Brand & Model Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('products.brand')}</Label>
              <select
                value={selectedBrandId}
                onChange={(e) => {
                  setSelectedBrandId(e.target.value);
                  setValue('modelId', '');
                }}
                disabled={isEditing}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t('products.selectBrand')}</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.code} - {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="modelId">{t('products.model')}</Label>
              <select
                id="modelId"
                {...register('modelId', { required: t('common.required') })}
                disabled={isEditing || !selectedBrandId}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t('products.selectModel')}</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.code} - {model.name}
                  </option>
                ))}
              </select>
              {errors.modelId && (
                <p className="text-sm text-red-500">{errors.modelId.message}</p>
              )}
            </div>
          </div>

          {/* Code & SKU */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('products.code')}</Label>
              <Input
                id="code"
                {...register('code', { required: t('common.required') })}
                placeholder="001"
                maxLength={3}
                disabled={isEditing}
              />
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
              <p className="text-xs text-muted-foreground">{t('products.codeHelp')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sku">{t('products.sku')}</Label>
              <Input
                id="sku"
                {...register('sku')}
                placeholder="SKU-001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">{t('products.sortOrder')}</Label>
              <Input
                id="sortOrder"
                type="number"
                {...register('sortOrder', { valueAsNumber: true })}
              />
            </div>
          </div>

          {/* Name & Slug */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('products.name')}</Label>
              <Input
                id="name"
                {...register('name', { required: t('common.required') })}
                placeholder="Product Name"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">{t('products.slug')}</Label>
              <Input
                id="slug"
                {...register('slug', { required: t('common.required') })}
                placeholder="product-name"
              />
              {errors.slug && (
                <p className="text-sm text-red-500">{errors.slug.message}</p>
              )}
            </div>
          </div>

          {/* Short Description */}
          <div className="space-y-2">
            <Label htmlFor="shortDescription">{t('products.shortDescription')}</Label>
            <Input
              id="shortDescription"
              {...register('shortDescription')}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('products.description')}</Label>
            <Textarea
              id="description"
              {...register('description')}
              rows={4}
            />
          </div>

          {/* Price */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="priceOnRequest"
                  {...register('priceOnRequest')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="priceOnRequest">{t('products.priceOnRequest')}</Label>
              </div>
            </div>

            {!watchPriceOnRequest && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="price">{t('products.price')}</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    {...register('price', { valueAsNumber: true })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">{t('products.currency')}</Label>
                  <select
                    id="currency"
                    {...register('currency')}
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Stock Status & Min Order */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="stockStatus">{t('products.stockStatus')}</Label>
              <select
                id="stockStatus"
                {...register('stockStatus')}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="IN_STOCK">{t('products.inStock')}</option>
                <option value="LOW_STOCK">{t('products.lowStock')}</option>
                <option value="OUT_OF_STOCK">{t('products.outOfStock')}</option>
                <option value="PRE_ORDER">{t('products.preOrder')}</option>
                <option value="DISCONTINUED">{t('products.discontinued')}</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="minOrderQty">{t('products.minOrderQty')}</Label>
              <Input
                id="minOrderQty"
                type="number"
                min={1}
                {...register('minOrderQty', { valueAsNumber: true })}
              />
            </div>
          </div>

          {/* Specifications */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('products.specifications')}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addSpecification}>
                <Plus className="mr-1 h-3 w-3" />
                {t('products.addSpec')}
              </Button>
            </div>
            {specifications.map((spec, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={t('products.specKey')}
                  value={spec.key}
                  onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder={t('products.specValue')}
                  value={spec.value}
                  onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSpecification(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>

          {/* Status Flags */}
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isActive">{t('products.active')}</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isFeatured"
                {...register('isFeatured')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isFeatured">{t('products.featured')}</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !watchModelId}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
